import React from 'react';
import { motion } from 'framer-motion';
import type { FranchiseCardProps } from './types';
import { formatPhoneNumber, getFranchiseStatus, getTodayHours } from '../../utils/salonUtils';

/**
 * FranchiseCard Component
 * Displays franchise information with selection capability
 */
const FranchiseCard: React.FC<FranchiseCardProps> = ({
  franchise,
  onSelect,
  variant = 'default',
}) => {
  const status = getFranchiseStatus(franchise);
  const todayHours = getTodayHours(franchise.working_hours, franchise.timezone);

  const handleSelect = () => {
    onSelect(franchise);
  };

  if (variant === 'compact') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow cursor-pointer"
        onClick={handleSelect}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {franchise.name}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {franchise.address}
            </p>
          </div>
          <button
            className="ml-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            onClick={handleSelect}
          >
            Select
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
    >
      {/* Banner Image */}
      {franchise.banner && (
        <div className="relative h-48 bg-gradient-to-r from-primary to-primary-dark">
          <img
            src={franchise.banner.url}
            alt={franchise.banner.alt || franchise.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          
          {/* Status Badge */}
          <div className="absolute top-4 right-4">
            <span
              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                status.color === 'green'
                  ? 'bg-green-500 text-white'
                  : status.color === 'red'
                  ? 'bg-red-500 text-white'
                  : 'bg-gray-500 text-white'
              }`}
            >
              {status.text}
            </span>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-6">
        {/* Logo & Name */}
        <div className="flex items-start gap-4 mb-4">
          {franchise.logo && (
            <div className="flex-shrink-0">
              <img
                src={typeof franchise.logo === 'string' ? franchise.logo : franchise.logo.url}
                alt={franchise.name}
                className="w-16 h-16 rounded-lg object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}
          
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              {franchise.name}
            </h3>
            {franchise.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {franchise.description}
              </p>
            )}
          </div>
        </div>

        {/* Details */}
        <div className="space-y-3 mb-6">
          {/* Address */}
          <div className="flex items-start gap-3">
            <span className="material-symbols-outlined text-primary text-xl flex-shrink-0">
              location_on
            </span>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {franchise.address}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {franchise.state}, {franchise.country} {franchise.zipcode}
              </p>
            </div>
          </div>

          {/* Phone */}
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined text-primary text-xl flex-shrink-0">
              phone
            </span>
            <a
              href={`tel:${franchise.phone}`}
              className="text-sm text-gray-900 dark:text-white hover:text-primary transition-colors"
            >
              {formatPhoneNumber(franchise.phone)}
            </a>
          </div>

          {/* Today's Hours */}
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined text-primary text-xl flex-shrink-0">
              schedule
            </span>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Today: {todayHours}
              </p>
            </div>
          </div>

          {/* Capacity */}
          {franchise.capacity && (
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-primary text-xl flex-shrink-0">
                groups
              </span>
              <p className="text-sm text-gray-900 dark:text-white">
                Capacity: {franchise.capacity} guests
              </p>
            </div>
          )}
        </div>

        {/* Select Button */}
        <button
          onClick={handleSelect}
          className="w-full py-3 px-6 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors flex items-center justify-center gap-2"
        >
          <span>Select This Location</span>
          <span className="material-symbols-outlined">arrow_forward</span>
        </button>
      </div>
    </motion.div>
  );
};

export default FranchiseCard;
